<script setup lang="ts">

import {computed, inject, ref, watch} from "vue";
import {useStore} from "vuex";
import {useI18n} from "vue-i18n";
import {ParametersVariations} from "@/assets/ts/parametersVariations";
import ItemV3StonesParameterLoading
  from "@/components/itemV3/parameter/stones/parameter/itemV3StonesParameterLoading.vue";

const props = withDefaults(defineProps<{
  groupIdx: number;
  hideSpreading?: boolean;
}>(), {
  hideSpreading: false,
});
const emit = defineEmits(["patch"]);

const store = useStore();
const {t} = useI18n();

const loadItemVariants = inject<any>("loadItemVariants");

const itemIdx = inject<any>("itemIdx");
const session = inject<any>("session");

/**
 * session
 *
 * Transferred convention from itemV3StonesSetting.vue / itemV3StonesStoneSize.vue:
 * configureAll/itemIdx are wired into ParametersVariations the same way, but the variants
 * are fetched per stone group via getItemVariants(itemIdx, "stones.<groupIdx>.stone_number")
 * / getItemVariants(itemIdx, "stones.<groupIdx>.stone_spreading").
 */

const configureAll = computed(() => store.getters["session/getConfigureAll"])
const variantIdx = ""

const parametersVariations = new ParametersVariations(variantIdx, configureAll, session, itemIdx, loadItemVariants)

/**
 * stone number - stones.<groupIdx>.stone_number
 * stone spreading - stones.<groupIdx>.stone_spreading
 */

const stoneNumber = ref({
  "stone_number": {},
  "stone_spreading": {}
} as any)

const getStoneNumberData = (groupIdy: number) => {
  if (groupIdy >= 0) {
    stoneNumber.value.stone_number = parametersVariations.getItemVariants(itemIdx.value, "stones." + groupIdy + ".stone_number")
    stoneNumber.value.stone_spreading = parametersVariations.getItemVariants(itemIdx.value, "stones." + groupIdy + ".stone_spreading")
  }
}

watch(() => props.groupIdx, (groupIdy) => {
  getStoneNumberData(groupIdy)
}, {immediate: true})

watch(session, () => {
  getStoneNumberData(props.groupIdx)
}, {deep: true})

/**
 * stone number variants
 */

const numberActive = computed(() => {
  return stoneNumber.value.stone_number[0]
})

const numberValuesList = computed(() => numberActive.value?.values || [])

const matchedNumberValue = computed(() => numberValuesList.value.find((v: any) => v.match))

// pre-selected stone numbers, always shown first (in this order) when available
const quickNumberValues = [1, 3, 5, 7]

const getQuickNumberValue = (idx: number) => numberValuesList.value.find((v: any) => v.idx === idx)

const isValidNumberIdx = (idx: number) => numberValuesList.value.some((v: any) => v.idx === idx)

/**
 * stone number - step stepper (transferred from itemV3StonesStoneSize.vue's "stone size -
 * step slider"), lets the user move to the previous/next valid stone number instead of only
 * being able to pick one of the quick values or type a number manually
 */

const sortedNumberIdxs = computed(() => numberValuesList.value
    .map((v: any) => Number(v.idx))
    .sort((a: number, b: number) => a - b))

const currentNumberPos = computed(() => {
  if (!matchedNumberValue.value) return -1
  return sortedNumberIdxs.value.indexOf(Number(matchedNumberValue.value.idx))
})

const stepDownDisabled = computed(() => currentNumberPos.value <= 0)

const stepUpDisabled = computed(() => currentNumberPos.value !== -1
    ? currentNumberPos.value >= sortedNumberIdxs.value.length - 1
    : sortedNumberIdxs.value.length === 0)

const stepNumber = (delta: number) => {
  if (!numberActive.value || !sortedNumberIdxs.value.length) return

  const newPos = currentNumberPos.value === -1 ? 0 : currentNumberPos.value + delta
  if (newPos < 0 || newPos >= sortedNumberIdxs.value.length) return

  const newIdx = sortedNumberIdxs.value[newPos]
  const newValue = numberValuesList.value.find((v: any) => Number(v.idx) === newIdx)
  if (newValue) handleValuePatch(numberActive.value.idx, newValue)
}

/**
 * stone spreading variants
 */

const spreadingActive = computed(() => stoneNumber.value.stone_spreading[0])

const spreadingValuesList = computed(() => spreadingActive.value?.values || [])

// "full"/"half"/"three-quarters" are shown as quick options next to the stone number
// quick picks, with fixed translated display names (independent of the variant's own title)
const quickSpreadingIdx = ["full", "half", "three-quarters"]

const quickSpreadingLabels = computed<Record<string, string>>(() => ({
  full: t('components.parameters.stones.number.spreading_full'),
  half: t('components.parameters.stones.number.spreading_half'),
  "three-quarters": t('components.parameters.stones.number.spreading_three_quarters'),
}))

const getQuickSpreadingValue = (idx: string) => spreadingValuesList.value.find((v: any) => v.idx === idx)

// the remaining spreading options are hidden behind a dropdown (transferred from
// itemV3StonesPosition.vue's "position - object" dropdown)
const otherSpreadingValues = computed(() => spreadingValuesList.value.filter((v: any) => !quickSpreadingIdx.includes(v.idx)))

const matchedOtherSpreadingValue = computed(() => otherSpreadingValues.value.find((v: any) => v.match))

// whether the dropdown with the remaining spreading options is open
const showSpreadingDropdown = ref(false)

/**
 * manual stone number input - allows any stone number that is a valid value idx in stone_number
 */

const manualNumber = ref(0)

watch(matchedNumberValue, (value) => {
  manualNumber.value = value ? value.idx : ""
}, {immediate: true})

const onManualNumberChange = () => {
  const idx: number = Number(manualNumber.value) || 0

  if (idx && isValidNumberIdx(idx)) {
    if (!matchedNumberValue.value || matchedNumberValue.value.idx !== idx) {
      handleValuePatch(numberActive.value.idx, {idx: idx})
    }
  } else {
    // not a valid stone number option - reset back to the currently matched value
    manualNumber.value = matchedNumberValue.value ? matchedNumberValue.value.idx : ""
  }
}

/**
 * Patch
 */

const handleValuePatch = (actionIdx: string, value: any) => {
  emit('patch', {
    action: actionIdx,
    value: value.idx,
    certificated_diamond: value.certificated_diamond
  });
};

</script>

<template>
  <div v-if="numberActive || spreadingActive" class="w-full my-4">

    <!-- stone number -->
    <div v-if="numberActive">
      <ul class="flex gap-1.5 flex-wrap mb-2 w-full justify-center md:justify-end">
        <!-- preselected stone numbers - 1 / 3 / 5 / 7 -->
        <template v-for="numberIdx in quickNumberValues">
          <li v-if="getQuickNumberValue(numberIdx)" :key="'number-' + numberIdx">
            <button
                type="button"
                class="flex size-9 cursor-pointer items-center justify-center rounded-full border text-xs font-medium transition-colors"
                :class="getQuickNumberValue(numberIdx).match ? 'border-2 border-brand-main bg-[#f3f0e9] text-brand-main' : 'border-[#e7ddcc] text-parameter-text hover:bg-[#f3f0e9]'"
                @click="handleValuePatch(numberActive.idx, getQuickNumberValue(numberIdx))"
            >
              {{ numberIdx }}
            </button>
          </li>
        </template>

        <!-- divider between the stone numbers and the stone spreading quick picks -->
        <li v-if="quickSpreadingIdx.some((spreadingIdx) => getQuickSpreadingValue(spreadingIdx))"
            class="flex items-center px-0.5">
          <span class="h-6 w-px bg-gray-300"></span>
        </li>

        <!-- preselected stone spreading - full / half / three-quarters -->
        <template v-for="spreadingIdx in quickSpreadingIdx">
          <li v-if="getQuickSpreadingValue(spreadingIdx)" :key="'spreading-' + spreadingIdx">
            <button
                type="button"
                class="flex h-9 cursor-pointer items-center justify-center whitespace-nowrap rounded-full border px-3 text-xs font-medium transition-colors"
                :class="getQuickSpreadingValue(spreadingIdx).match ? 'border-2 border-brand-main bg-[#f3f0e9] text-brand-main' : 'border-[#e7ddcc] text-parameter-text hover:bg-[#f3f0e9]'"
                @click="handleValuePatch(spreadingActive.idx, getQuickSpreadingValue(spreadingIdx))"
            >
              {{ quickSpreadingLabels[spreadingIdx] }}
            </button>
          </li>
        </template>
      </ul>

      <!-- stepper and the stone spreading dropdown sit next to each other in one row -->
      <div class="flex items-start gap-2">
        <!-- stepper - allows stepping to the previous/next valid stone number or typing one in directly -->
        <div class="flex flex-none items-center gap-2">
          <button
              type="button"
              class="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-[#e7ddcc] text-[#8f8a80] transition-colors hover:bg-[#f3f0e9] hover:text-brand-main disabled:cursor-not-allowed disabled:opacity-30"
              :disabled="stepDownDisabled"
              @click="stepNumber(-1)"
          >
            −
          </button>
          <input
              type="text"
              inputmode="numeric"
              v-model="manualNumber"
              @change="onManualNumberChange"
              @keyup.enter="onManualNumberChange"
              class="flex-1 rounded-xl w-15 border border-[#e7ddcc] py-1.5 px-3 text-center text-xs font-medium text-parameter-title focus:border-brand-main focus:outline-none"
          />
          <button
              type="button"
              class="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-[#e7ddcc] text-[#8f8a80] transition-colors hover:bg-[#f3f0e9] hover:text-brand-main disabled:cursor-not-allowed disabled:opacity-30"
              :disabled="stepUpDisabled"
              @click="stepNumber(1)"
          >
            +
          </button>
        </div>

        <!-- stone spreading - remaining options, shown as a dropdown (transferred from
        itemV3StonesPosition.vue's "position - object" dropdown) -->
        <div v-if="!hideSpreading && spreadingActive && otherSpreadingValues.length"
             class="grow overflow-hidden rounded-2xl border border-[#e7ddcc] bg-white">
          <button
              type="button"
              class="flex w-full cursor-pointer items-center justify-between px-3 py-2 text-left text-xs text-parameter-title transition-colors hover:bg-[#f3f0e9]"
              @click="showSpreadingDropdown = !showSpreadingDropdown"
          >
            <span>{{ matchedOtherSpreadingValue?.title || $t('components.parameters.stones.number.spreading_fallback') }}</span>
            <svg xmlns="http://www.w3.org/2000/svg" class="size-3.5 shrink-0 transition-transform"
                 :class="showSpreadingDropdown ? 'rotate-180' : ''"
                 viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                 stroke-linejoin="round">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          <ul v-if="showSpreadingDropdown" class="border-t border-[#e7ddcc] py-1">
            <li v-for="(variantData, otherSpreadingIdx) in otherSpreadingValues" :key="otherSpreadingIdx"
                class="mx-1.5 cursor-pointer rounded-xl px-2.5 py-2 text-xs transition-colors hover:bg-[#f3f0e9]"
                :class="variantData.match ? 'bg-[#e7ddcc] font-medium text-parameter-title' : 'text-parameter-text'"
                @click="handleValuePatch(spreadingActive.idx, variantData); showSpreadingDropdown = false"
            >
              {{ variantData.title }}
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
  <item-v3-stones-parameter-loading v-else/>
</template>

<style scoped>
</style>
